import React, { useEffect, useState } from "react";
import {
  useDeleteCourseMutation,
  useGetCourseCatalogAdminSideQuery,
  useLazyToggleCourseVisibilityQuery,
} from "../../services/course/courseApi";
import { useNavigate } from "react-router-dom";
import { EllipsisVertical, GraduationCap, Trash } from "../AdminIcons";
import { showToast } from "../../utils/toast";
import useClickOutsideModal from "../common/useClickOutsideModal";
import DeleteCourseModal from "./DeleteCourseModal";
import Loader from "../common/Loader";

const PAGE_SIZE = 6;

const CoursesList = ({
  selectedCategoryId = "",
  selectedStatus = "",
  priceMin = "",
  priceMax = "",
}) => {
  const navigate = useNavigate();
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [togglingCourseId, setTogglingCourseId] = useState(null);
  const [openCourseId, setOpenCourseId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId, selectedStatus, priceMin, priceMax]);

  const {
    currentData: courseCatalogResponse,
    isFetching: isCatalogFetching,
    isLoading: isCatalogLoading,
    refetch: refetchCourseCatalog,
  } = useGetCourseCatalogAdminSideQuery({
    page: currentPage,
    limit: PAGE_SIZE,
    category: selectedCategoryId,
    status: selectedStatus,
    priceMin,
    priceMax,
  });

  const [triggerToggleCourseVisibility] = useLazyToggleCourseVisibilityQuery();

  const [deleteCourse, { isLoading: isDeletingCourse }] =
    useDeleteCourseMutation();
  const courses = (courseCatalogResponse?.data || []).map(
    (course) => ({
      id: course?._id,
      title: course?.courseTitle || "Untitled course",

      price: course?.coursePrice ? `Rs${course.coursePrice}` : "Rs0",
      type: course?.courseEnrollementType || "course",
      active: Boolean(course?.courseVisibility),
      thumbnail: course?.courseThumbnail?.secureUrl || "",
      level: course?.courseLevel?.title || "",
    }),
  );

  const totalCourses = courseCatalogResponse?.pagination?.totalCourses || 0;

  const totalPages = Math.ceil(totalCourses / PAGE_SIZE);
  const handleToggleCourseVisibility = async (courseId) => {
    if (!courseId || togglingCourseId) {
      return;
    }

    try {
      setTogglingCourseId(courseId);
      const response = await triggerToggleCourseVisibility(courseId).unwrap();

      showToast({
        message: response?.message || "Course visibility updated successfully!",
        type: "success",
      });
      refetchCourseCatalog();
    } catch (error) {
      console.error("Unable to toggle course visibility:", error);
      showToast({
        message:
          error?.data?.message ||
          error?.message ||
          "Unable to update course visibility.",
        type: "error",
      });
    } finally {
      setTogglingCourseId(null);
    }
  };
  const handleConfirmDelete = async () => {
    const courseId = courseToDelete?._id || courseToDelete?.id;
    if (!courseId) return;

    try {
      const response = await deleteCourse(courseId).unwrap();

      setCourseToDelete(null);
      showToast({
        message: response?.message || "Course deleted successfully!",
        type: "success",
      });
      refetchCourseCatalog();
      if (courses.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      }
    } catch (error) {
      console.error("Failed to delete course:", error);
      setCourseToDelete(null);
      showToast({
        message:
          error?.data?.message ||
          error?.message ||
          "Failed to delete course. Please try again.",
        type: "error",
      });
    }
  };
  const modalRef = useClickOutsideModal(() => setOpenCourseId(null));
  return (
    <div>
      {isCatalogLoading ? (
        <div className="flex justify-center pt-10">
        <Loader size='lg' className="text-[#923D8C]" />
        </div>
      ) : courses.length === 0 ? (
        <div className="mt-5 flex justify-center rounded-[10px] bg-[#F5F5F5] py-16 text-sm text-[#6B7280]">
          No courses found.
        </div>
      ) : (
        <div className="mt-5 max-w-[1480px] grid gap-4 2xl:gap-9 grid-cols-2 sm:grid-cols-3 xl:grid-cols-4">
          {courses.map((course) => (
            <article
              key={course.id}
              onClick={() => {
                navigate(`/admin/course-management/course-detail/${course.id}`);
              }}
              className="overflow-hidden rounded-[10px] bg-[#F5F5F5] transition duration-200 hover:-translate-y-1 hover:shadow-[0_10px_22px_rgba(0,0,0,0.12)]  cursor-pointer
            "
            >
              <div className="relative bg-[#923D8C0D] rounded-[10px] h-36 2xl:h-[177px]  2xl:py-5">
                <div className="absolute inset-0  flex items-center justify-center overflow-hidden ">
                  <img
                    src={course.thumbnail}
                    alt="title thumbnail for course"
                    className="w-full object-cover  h-full  "
                  />
                </div>
              </div>

              <div className=" px-2 pb-6 mt-3 2xl:mt-[18px] ">
                <div className="flex items-start justify-between gap-4">
                  <h3
                    title={course.title}
                    className="line-clamp-2 h-11 max-w-[180px] text-lg font-medium leading-[21px] text-[#111111]"
                  >
                    {course.title}
                  </h3>

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleCourseVisibility(course.id);
                    }}
                    disabled={togglingCourseId === course.id}
                    className={`relative  h-5 w-[46px] rounded-full transition ${
                      course.active ? "bg-[#923D8C]" : "bg-[#B9B4BF]"
                    } ${togglingCourseId === course.id ? "opacity-60" : ""}`}
                  >
                    <span
                      className={`absolute -top-[0.9px] h-[19.5px] w-5 rounded-full bg-white  transition ${
                        course.active ? "right-0.5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>

                <span className="inline-flex mt-3 2xl:mt-5 rounded-[5px] bg-[#923D8C33] px-[18px] py-1 text-[11px] font-medium text-[#923D8C]">
                  {course.type}
                </span>

                <p className="mt-[22px] flex items-center gap-2.5 text-[15px] leading-[18px] text-[#6B7280]">
                  <GraduationCap />
                  <span>Level: {course.level}</span>
                </p>

                <div className="flex mt-5 items-center justify-between">
                  <p className="text-lg font-medium leading-[21px] text-[#111111]">
                    {course.price}
                  </p>

                  <div className="flex items-center gap-5">
                    <button
                      type="button"
                      aria-label={`Delete ${course.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setCourseToDelete(course);
                      }}
                    >
                      <Trash />
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenCourseId((prev) =>
                            prev === course.id ? null : course.id,
                          );
                        }}
                      >
                        <EllipsisVertical />
                      </button>

                      {openCourseId === course.id && (
                        <div
                          ref={modalRef}
                          onClick={(event) => event.stopPropagation()}
                          className="absolute right-0 bottom-full z-30  w-[114px] border border-gray-200 bg-white  "
                        >
                          <button
                            type="button"
                            className="block w-full pl-3 pt-[14px] pb-2 text-left text-base leading-[19px] text-[#111111] transition hover:bg-[#F5F5F5]"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/admin/course-management/course-detail/${course.id}`,
                              );
                            }}
                          >
                            View Detail
                          </button>
                          <button
                            type="button"
                            className="block w-full pl-3  pb-[14px] pt-2 text-left text-base leading-[19px] text-[#111111] transition hover:bg-[#F5F5F5]"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/admin/course-management/course-detail/${course.id}`,
                              );
                            }}
                          >
                            Update
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex w-full max-w-[1480px] items-center justify-between px-2">
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage <= 1 || isCatalogFetching || isCatalogLoading}
            className={`rounded-full px-6 py-1 text-sm ${
              currentPage <= 1 || isCatalogFetching || isCatalogLoading
                ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                : "bg-[#923D8C] text-white"
            }`}
          >
            Prev
          </button>

          <p className="text-base font-medium text-black">
            Page {currentPage}/{totalPages}
          </p>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={
              currentPage >= totalPages || isCatalogFetching || isCatalogLoading
            }
            className={`rounded-full px-6 py-1 text-sm ${
              currentPage >= totalPages || isCatalogFetching || isCatalogLoading
                ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                : "bg-[#923D8C] text-white"
            }`}
          >
            Next
          </button>
        </div>
      )}

      <DeleteCourseModal
        isOpen={Boolean(courseToDelete)}
        onClose={() => setCourseToDelete(null)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingCourse}
      />
    </div>
  );
};

export default CoursesList;
